// Mirror of worker/src/llm/models.ts. Must stay in sync, the client picks
// a model up front so reel 1 can land instantly on the correct logo while
// the API generates the idea content.

export type Tier = "cheap" | "mid" | "premium" | "joker";

export interface ModelDef {
  id: string;
  label: string;
  tier: Tier;
  weight: number;
  streaming?: boolean;
}

export const MODELS: ModelDef[] = [
  // cheap (50%)
  { id: "google/gemini-3.1-pro-preview", label: "Gemini 3.1 Pro", tier: "cheap", weight: 3, streaming: false },
  { id: "x-ai/grok-4.3",                 label: "Grok 4.3",       tier: "cheap", weight: 3 },
  { id: "openai/gpt-5.4",                label: "GPT-5.4",        tier: "cheap", weight: 2, streaming: false },
  { id: "deepseek/deepseek-v4-flash",    label: "DeepSeek V4",    tier: "cheap", weight: 2, streaming: false },
  // mid (33%), Claude Haiku 4.5 dropped 2026-05-15: too much TARGET
  // MARKET drift on cheap+mid tier (~30% rejection rate by validator).
  // Pool here MUST mirror the worker's pool in worker/src/llm/models.ts;
  // a label here that the worker doesn't know about leads to an empty
  // reel cell once the meta event arrives with a different model.
  { id: "moonshotai/kimi-k2.6",          label: "Kimi K2.6",      tier: "mid",   weight: 3, streaming: false },
  { id: "z-ai/glm-5.1",                  label: "GLM 5.1",        tier: "mid",   weight: 2, streaming: false },
  // premium (15%)
  { id: "anthropic/claude-opus-4.7",     label: "Opus 4.7",       tier: "premium", weight: 2 },
  { id: "anthropic/claude-sonnet-4.6",   label: "Sonnet 4.6",     tier: "premium", weight: 1, streaming: false },
  // joker (2%)
  { id: "openai/gpt-5.5",                label: "GPT-5.5",        tier: "joker", weight: 1, streaming: false },
];

export const TIER_PROBABILITY: Record<Tier, number> = {
  cheap: 0.5,
  mid: 0.33,
  premium: 0.15,
  joker: 0.02,
};

export const MODEL_LABELS: string[] = MODELS.map((m) => m.label);

function pickFromArray<T extends { weight: number }>(arr: T[]): T {
  const total = arr.reduce((s, x) => s + x.weight, 0);
  let r = Math.random() * total;
  for (const x of arr) {
    r -= x.weight;
    if (r <= 0) return x;
  }
  return arr[arr.length - 1];
}

function pickTier(): Tier {
  const r = Math.random();
  let acc = 0;
  for (const t of ["cheap", "mid", "premium", "joker"] as const) {
    acc += TIER_PROBABILITY[t];
    if (r < acc) return t;
  }
  return "cheap";
}

/**
 * Pick a model from the right pool.
 *
 *   pool="full", every tier (cheap, mid, premium, joker).
 *   pool="free", cheap + mid only, premium/joker re-rolled into mid.
 *
 * Mirrors worker/src/llm/models.ts. Free-pool users used to get a
 * premium label pre-rendered on reel 1, and then the worker would
 * silently downgrade to a cheap model, the meta event arrived after
 * reel 1 had already landed and the modal showed a different model
 * than the reel (P0: 2026-05-15).
 */
export function pickModel(pool: "full" | "free" = "full"): ModelDef {
  if (pool === "free") {
    const tier: Tier = Math.random() < 0.6 ? "cheap" : "mid";
    return pickFromArray(MODELS.filter((m) => m.tier === tier));
  }
  const tier = pickTier();
  const pickPool = MODELS.filter((m) => m.tier === tier);
  return pickFromArray(pickPool);
}

export function modelIndex(label: string): number {
  return MODEL_LABELS.indexOf(label);
}
